import { useState, useRef, useEffect, useCallback } from "react";

interface Position {
  x: number;
  y: number;
}

interface UsePIPDragOptions {
  containerRef: React.RefObject<HTMLDivElement | null>;
  isDraggable: boolean;
  pipPosition: "bottom-right" | "top-right";
  onClick?: () => void;
}

interface UsePIPDragReturn {
  position: Position | null;
  isDragging: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
  handlePIPClick: (e: React.MouseEvent) => void;
  getDragClassName: (baseClasses: string, positionClasses: string, interactiveClasses: string) => string;
  getDragStyle: () => React.CSSProperties;
}

export default function usePIPDrag({
  containerRef,
  isDraggable,
  pipPosition,
  onClick,
}: UsePIPDragOptions): UsePIPDragReturn {
  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({
 x: 0,
y: 0 
});
  const [position, setPosition] = useState<Position | null>(null);
  const [hasMoved, setHasMoved] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const mouseDownPosRef = useRef<Position | null>(null);
  const wasDragRef = useRef(false);
  const dragPositionRef = useRef<Position | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const containerSizeRef = useRef<{ width: number; height: number } | null>(null);
  const resizeRafIdRef = useRef<number | null>(null);
  const isResizingRef = useRef(false);
  const resizePositionRef = useRef<Position | null>(null);
  const resizeTimeoutRef = useRef<number | null>(null);
  const clickHandledByMouseUpRef = useRef(false);

  // Calculate snap points (corners)
  // Note: bottom-right is adjusted to be above the local video PIP
  const getSnapPoints = useCallback(() => {
    if (!containerRef.current || !isDraggable) return [];

    const container = containerRef.current.parentElement;
    if (!container) return [];

    const containerRect = container.getBoundingClientRect();
    
    // Store container size for resize calculations
    containerSizeRef.current = {
 width: containerRect.width,
height: containerRect.height 
};
    
    const pipWidth = 240; // w-60 = 240px
    const pipHeight = 160; // h-40 = 160px
    const margin = 24; // 6 * 4px = 24px
    const localPipHeight = 160; // Local video PIP is also h-40 = 160px

    // Bottom-right snap point is positioned above the local video PIP
    // Local PIP is at bottom-6 right-6, so we position camera PIP above it
    let bottomRightY =
      containerRect.height - pipHeight - margin - localPipHeight - margin;

    // Ensure bottom-right Y doesn't go negative (fallback to regular bottom-right if container is too small)
    if (bottomRightY < margin) {
      bottomRightY = containerRect.height - pipHeight - margin;
    }

    return [
      {
 x: margin,
y: margin 
}, // top-left
      {
 x: containerRect.width - pipWidth - margin,
y: margin 
}, // top-right
      {
 x: margin,
y: containerRect.height - pipHeight - margin 
}, // bottom-left
      {
        x: containerRect.width - pipWidth - margin,
        y: bottomRightY,
      }, // bottom-right (above local PIP)
    ];
  }, [isDraggable, containerRef]);

  // Find nearest snap point - always snap to a corner (no free positioning)
  const snapToNearest = useCallback(
    (x: number, y: number) => {
      const snapPoints = getSnapPoints();
      if (snapPoints.length === 0) {
        // Fallback to first snap point if calculation fails
        return {
 x: 24,
y: 24 
};
      }

      let nearest = snapPoints[0];
      let minDistance = Math.sqrt(
        Math.pow(x - nearest.x, 2) + Math.pow(y - nearest.y, 2)
      );

      for (const point of snapPoints) {
        const distance = Math.sqrt(
          Math.pow(x - point.x, 2) + Math.pow(y - point.y, 2)
        );
        if (distance < minDistance) {
          minDistance = distance;
          nearest = point;
        }
      }

      // Always snap to the nearest corner (no threshold - always snap)
      return nearest;
    },
    [getSnapPoints]
  );

  // Keep dragPositionRef in sync with position state
  useEffect(() => {
    if (position && !isDragging && !isResizing) {
      dragPositionRef.current = position;
    }
  }, [position, isDragging, isResizing]);

  // Initialize position from pipPosition if not set
  useEffect(() => {
    if (isDraggable && position === null && containerRef.current) {
      const snapPoints = getSnapPoints();
      if (snapPoints.length >= 4) {
        // Use snap points for initial positioning
        let initialPos = snapPoints[0]; // default to top-left
        if (pipPosition === "top-right") {
          initialPos = snapPoints[1]; // top-right
        } else {
          // For bottom-right, use the adjusted snap point (above local PIP)
          initialPos = snapPoints[3]; // bottom-right (adjusted)
        }
        setPosition(initialPos);
        dragPositionRef.current = initialPos; // Initialize ref as well
        
        // Store initial container size
        const container = containerRef.current.parentElement;
        if (container) {
          const rect = container.getBoundingClientRect();
          containerSizeRef.current = {
 width: rect.width,
height: rect.height 
};
        }
      }
    }
  }, [isDraggable, pipPosition, position, getSnapPoints, containerRef]);

  // Handle container resize - maintain relative position
  useEffect(() => {
    if (!isDraggable || !containerRef.current || !position) return;

    const container = containerRef.current.parentElement;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      // Skip if we're currently dragging
      if (isDragging) return;

      const entry = entries[0];
      if (!entry) return;

      const newWidth = entry.contentRect.width;
      const newHeight = entry.contentRect.height;

      // If we don't have a previous size, just store the new size and return
      if (!containerSizeRef.current) {
        containerSizeRef.current = {
 width: newWidth,
height: newHeight 
};
        return;
      }

      const oldWidth = containerSizeRef.current.width;
      const oldHeight = containerSizeRef.current.height;

      // If size hasn't changed significantly, skip (threshold of 1px to avoid micro-adjustments)
      const widthDiff = Math.abs(newWidth - oldWidth);
      const heightDiff = Math.abs(newHeight - oldHeight);
      if (widthDiff < 1 && heightDiff < 1) return;

      // Cancel any pending resize animation frame or timeout
      if (resizeRafIdRef.current !== null) {
        cancelAnimationFrame(resizeRafIdRef.current);
      }
      if (resizeTimeoutRef.current !== null) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Mark that we're resizing
      isResizingRef.current = true;
      setIsResizing(true);

      // Use requestAnimationFrame to batch updates and prevent flickering
      resizeRafIdRef.current = requestAnimationFrame(() => {
        if (!containerRef.current) {
          resizeRafIdRef.current = null;
          return;
        }

        // Use the current position from ref (always up-to-date) or from resize ref
        const currentPosition = resizePositionRef.current || dragPositionRef.current;
        if (!currentPosition) {
          resizeRafIdRef.current = null;
          isResizingRef.current = false;
          setIsResizing(false);
          return;
        }

        // Calculate relative position (as percentage)
        const relativeX = currentPosition.x / oldWidth;
        const relativeY = currentPosition.y / oldHeight;

        // Calculate new absolute position
        const newX = relativeX * newWidth;
        const newY = relativeY * newHeight;

        // Snap to nearest corner with new dimensions
        const snapped = snapToNearest(newX, newY);

        // Store in ref for immediate use (don't update state yet to avoid re-render)
        resizePositionRef.current = snapped;
        dragPositionRef.current = snapped;

        // Update DOM directly with transition disabled to prevent flickering
        containerRef.current.style.transition = "none";
        containerRef.current.style.left = `${snapped.x}px`;
        containerRef.current.style.top = `${snapped.y}px`;

        // Store new container size
        containerSizeRef.current = {
 width: newWidth,
height: newHeight 
};

        // Debounce state update until resize is complete (prevents flickering from re-renders)
        resizeTimeoutRef.current = window.setTimeout(() => {
          // Update state after resize completes
          setPosition(snapped);
          
          // Re-enable transitions
          if (containerRef.current) {
            containerRef.current.style.transition = "left 0.2s ease-out, top 0.2s ease-out";
          }
          
          isResizingRef.current = false;
          setIsResizing(false);
          resizePositionRef.current = null;
          resizeTimeoutRef.current = null;
        }, 150);

        resizeRafIdRef.current = null;
      });
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      // Cancel any pending animation frame on cleanup
      if (resizeRafIdRef.current !== null) {
        cancelAnimationFrame(resizeRafIdRef.current);
        resizeRafIdRef.current = null;
      }
      // Clear any pending timeout
      if (resizeTimeoutRef.current !== null) {
        clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = null;
      }
      isResizingRef.current = false;
      setIsResizing(false);
      resizePositionRef.current = null;
    };
  }, [isDraggable, position, isDragging, snapToNearest, containerRef]);

  // Execute click action
  const executeClickAction = useCallback(() => {
    if (onClick) {
      onClick();
    }
  }, [onClick]);

  // Handle drag start
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isDraggable) return;

      // Store initial mouse position to detect if it's a click vs drag
      mouseDownPosRef.current = {
 x: e.clientX,
y: e.clientY 
};
      wasDragRef.current = false; // Reset drag flag
      clickHandledByMouseUpRef.current = false; // Reset click handled flag
      setIsDragging(true);
      setHasMoved(false);

      const container = containerRef.current?.parentElement;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const currentX = position?.x || 0;
        const currentY = position?.y || 0;
        setDragStart({
          x: e.clientX - containerRect.left - currentX,
          y: e.clientY - containerRect.top - currentY,
        });
      }
    },
    [isDraggable, position, containerRef]
  );

  // Handle drag
  useEffect(() => {
    if (!isDragging || !isDraggable) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;

      const container = containerRef.current.parentElement;
      if (!container) return;

      // Check if mouse moved significantly from initial position
      if (mouseDownPosRef.current && !hasMoved) {
        const deltaX = Math.abs(
          e.clientX - mouseDownPosRef.current.x
        );
        const deltaY = Math.abs(
          e.clientY - mouseDownPosRef.current.y
        );
        if (deltaX > 10 || deltaY > 10) {
          setHasMoved(true);
        }
      }

      const containerRect = container.getBoundingClientRect();
      const pipWidth = 240;
      const pipHeight = 160;

      let newX = e.clientX - dragStart.x - containerRect.left;
      let newY = e.clientY - dragStart.y - containerRect.top;

      // Constrain to container bounds
      newX = Math.max(0, Math.min(newX, containerRect.width - pipWidth));
      newY = Math.max(0, Math.min(newY, containerRect.height - pipHeight));

      // Store in ref
      dragPositionRef.current = {
 x: newX,
y: newY 
};

      // Use requestAnimationFrame to batch DOM updates and prevent flickering
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }

      rafIdRef.current = requestAnimationFrame(() => {
        if (containerRef.current && dragPositionRef.current) {
          containerRef.current.style.left = `${dragPositionRef.current.x}px`;
          containerRef.current.style.top = `${dragPositionRef.current.y}px`;
          containerRef.current.style.transition = "none"; // Disable transition during drag
          containerRef.current.style.willChange = "transform"; // Hint to browser for optimization
        }
        rafIdRef.current = null;
      });
    };

    const handleMouseUp = () => {
      if (isDragging) {
        const wasDrag = hasMoved;
        wasDragRef.current = wasDrag; // Store in ref for synchronous access

        if (wasDrag && dragPositionRef.current) {
          // Only snap if we actually dragged
          const snapped = snapToNearest(
            dragPositionRef.current.x,
            dragPositionRef.current.y
          );

          // Update state with snapped position (this will trigger one final re-render)
          setPosition(snapped);

          // Update DOM with snapped position and re-enable transition
          if (containerRef.current) {
            containerRef.current.style.left = `${snapped.x}px`;
            containerRef.current.style.top = `${snapped.y}px`;
            containerRef.current.style.transition =
              "left 0.2s ease-out, top 0.2s ease-out";
            containerRef.current.style.willChange = "auto"; // Reset will-change
          }
        } else if (!wasDrag) {
          // If it wasn't a drag, it was a click - execute the click action
          // Mark that mouseUp handled the click to prevent onClick from also firing
          // Set this synchronously so onClick (which fires after mouseUp) can check it
          clickHandledByMouseUpRef.current = true;
          // Use setTimeout to ensure this happens after React processes the event
          setTimeout(() => {
            if (!wasDragRef.current) {
              executeClickAction();
            }
            // Reset the flag after executing to allow future clicks
            setTimeout(() => {
              clickHandledByMouseUpRef.current = false;
            }, 50);
          }, 0);
        }

        setIsDragging(false);
        dragPositionRef.current = null;

        // Reset after a delay
        setTimeout(() => {
          setHasMoved(false);
          mouseDownPosRef.current = null;
          wasDragRef.current = false;
          clickHandledByMouseUpRef.current = false;
        }, 150);
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      // Cancel any pending animation frame
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [
    isDragging,
    isDraggable,
    dragStart,
    position,
    hasMoved,
    snapToNearest,
    executeClickAction,
    containerRef,
  ]);

  // Handle click
  const handlePIPClick = useCallback(
    (e: React.MouseEvent) => {
      // Prevent default click handling if it was a drag
      // The actual click action is handled in mouseUp if it wasn't a drag
      if (wasDragRef.current || hasMoved) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // If mouseUp already handled the click, don't execute again
      if (clickHandledByMouseUpRef.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Only execute if we're sure it wasn't a drag and mouseUp didn't handle it
      // This is a fallback for cases where mouseUp didn't fire (e.g., keyboard events)
      if (!isDragging) {
        executeClickAction();
      }
    },
    [hasMoved, isDragging, executeClickAction]
  );

  // Get drag-related className additions
  const getDragClassName = useCallback(
    (
      baseClasses: string,
      positionClasses: string,
      interactiveClasses: string
    ) => {
      const draggingClasses = isDragging
        ? "cursor-grabbing transition-none"
        : isDraggable
          ? "cursor-grab"
          : "";
      return `${baseClasses} ${positionClasses} ${interactiveClasses} ${draggingClasses}`.trim();
    },
    [isDragging, isDraggable]
  );

  // Get drag-related styles
  const getDragStyle = useCallback((): React.CSSProperties => {
    if (isDraggable && position) {
      // During resize, use resizePositionRef to avoid state updates causing flicker
      // During drag, use the ref value if available, otherwise use state
      let currentPos: Position;
      if (isResizing && resizePositionRef.current) {
        currentPos = resizePositionRef.current;
      } else if (isDragging && dragPositionRef.current) {
        currentPos = dragPositionRef.current;
      } else {
        currentPos = position;
      }

      // Disable transitions during drag or resize to prevent flickering
      const shouldDisableTransition = isDragging || isResizing;

      return {
        left: `${currentPos.x}px`,
        top: `${currentPos.y}px`,
        transition: shouldDisableTransition
          ? "none"
          : "left 0.2s ease-out, top 0.2s ease-out",
      };
    }
    return {};
  }, [isDraggable, position, isDragging, isResizing]);

  return {
    position,
    isDragging,
    handleMouseDown,
    handlePIPClick,
    getDragClassName,
    getDragStyle,
  };
}
